// language-manager.js — two-axis language state (Brief 004 / 004b).
//
// Generalises Unity's LanguageManager. Two INDEPENDENT axes (never one toggle —
// Gotcha 1):
//   • motherTongue (L1) — what the learner already speaks. Drives UI strings
//     (i18n), the translation scaffold, and the L1 scaffolding audio.
//   • targetLanguage (L2) — what the learner is learning. Drives the phrase
//     shown in the exercise and the audio that plays.
//
// The model is SYMMETRIC (Brief 004b): every language is described once and is
// usable as EITHER axis. An English speaker learns Mandarin (hear `zh_…`, match
// the image, English text as the scaffold); a Mandarin speaker learns English
// (the original direction). The only thing separating the two axes is which
// descriptor fields each one reads:
//   • target  → its `textField` (the word shown) + its audio resolver.
//   • L1       → its `textField` (the scaffold label) + its audio resolver,
//               suppressed entirely when L1 === target.
//
// Everything is read from content/languages.json so a new language is a
// DATA-ONLY add (one descriptor + i18n pack + audio set) — no engine or screen
// changes (Gotcha 3 / Success criterion 5). No language code is hardcoded here.
//
// localStorage keys SG_MotherTongue / SG_TargetLanguage match the plan so they
// stay greppable later (Gotcha 5).

import { mediaBase, audioExt } from "../data/media-paths.js";
import { setLanguage } from "../core/i18n.js";

const LS_MOTHER = "SG_MotherTongue";
const LS_TARGET = "SG_TargetLanguage";

/**
 * @typedef {object} LanguageEntry
 * @property {string} code                  canonical axis code, e.g. "en" / "zh"
 * @property {string} name                  native name (locale-correct as-is)
 * @property {string} i18n                  i18n pack key, e.g. "zh-Hans"
 * @property {string} textField             MMU field rendered in this language,
 *                                          e.g. "phrase" (en) / "phraseZh" (zh)
 * @property {string} audioPrefix           audio filename prefix, e.g. "zh_" ("" = none)
 * @property {string} audioKey              MMU field the audio key derives from,
 *                                          e.g. "audioStem" (en) / "phrase" (zh)
 * @property {boolean} audioFromPhrase      run PhraseNoSpaces() over audioKey's value
 *                                          (zh) vs use it verbatim (en, an explicit stem)
 * @property {boolean} availableAsTarget    offered as a course to learn
 * @property {boolean} availableAsMotherTongue  offered as an L1 in Step 1
 * @property {boolean} [defaultTarget]      target axis fallback
 * @property {boolean} [defaultMotherTongue] L1 axis fallback + i18n fallback
 */
/**
 * @typedef {object} LanguageRegistry
 * @property {LanguageEntry[]} languages
 */

// The opening dialogue (Brief 004a Step 1) must read correctly before any L1 is
// chosen, so it boots in the neutral lingua-franca pack rather than the default
// L1 — otherwise an English-speaking visitor is greeted in Mandarin (Brief 004
// Issue #1). English is already i18n.js's universal fallback, so it's the
// natural neutral. The default L1 still governs the i18n *fallback*; it no
// longer dictates the opening UI.
const NEUTRAL_OPENING_I18N = "en";

/** Fires a bare "change" Event whenever either axis changes (Unity OnLanguageChanged). */
export const OnLanguageChanged = new EventTarget();

/** @type {LanguageRegistry | null} */ let registry = null;
/** @type {Promise<LanguageRegistry> | null} */ let inFlight = null;
/** @type {string} */ let motherTongue = "";
/** @type {string} */ let targetLanguage = "";

/** Fetch + cache content/languages.json. Concurrent callers share one fetch.
 * @returns {Promise<LanguageRegistry>} */
function loadRegistry() {
  if (registry) return Promise.resolve(registry);
  if (inFlight) return inFlight;
  inFlight = fetch("/content/languages.json")
    .then((r) => {
      if (!r.ok) throw new Error(`[lang] languages.json HTTP ${r.status}`);
      return r.json();
    })
    .then((json) => {
      registry = /** @type {LanguageRegistry} */ (json);
      return registry;
    })
    .finally(() => { inFlight = null; });
  return inFlight;
}

/** Ensure the registry is loaded without re-applying the i18n pack (screens use
 * this so reading it never re-fires kfe:i18n / a shell remount).
 * @returns {Promise<void>} */
export async function ensureLoaded() { await loadRegistry(); }

/** The loaded registry. @returns {LanguageRegistry} */
export function languages() {
  if (!registry) throw new Error("[lang] not loaded — call initLanguages()/ensureLoaded() first");
  return registry;
}

/** Descriptor by code, or undefined. @param {string} code @returns {LanguageEntry | undefined} */
function entry(code) {
  return registry?.languages.find((l) => l.code === code);
}

/** Active L1 descriptor (falls back to the default L1). @returns {LanguageEntry} */
function motherTongueEntry() {
  const reg = languages();
  return entry(motherTongue)
      ?? reg.languages.find((l) => l.defaultMotherTongue)
      ?? reg.languages.find((l) => l.availableAsMotherTongue)
      ?? reg.languages[0];
}

/** Active target descriptor (falls back to the default target). @returns {LanguageEntry} */
function targetEntry() {
  const reg = languages();
  return entry(targetLanguage)
      ?? reg.languages.find((l) => l.defaultTarget)
      ?? reg.languages.find((l) => l.availableAsTarget)
      ?? reg.languages[0];
}

/** Default L1 code. @returns {string} */
function defaultMotherTongueCode() {
  const reg = languages();
  return (reg.languages.find((l) => l.defaultMotherTongue)
      ?? reg.languages.find((l) => l.availableAsMotherTongue)
      ?? reg.languages[0]).code;
}

/** Default target code. @returns {string} */
function defaultTargetCode() {
  const reg = languages();
  return (reg.languages.find((l) => l.defaultTarget)
      ?? reg.languages.find((l) => l.availableAsTarget)
      ?? reg.languages[0]).code;
}

/**
 * Normalise a stored axis code to a current descriptor code. Tolerates the
 * pre-004b Mandarin L1 code (`zh-Hans`, which was both the code and the i18n
 * pack) by mapping it onto the descriptor whose i18n pack matches — so returning
 * users keep their choice instead of being reset (Success criterion 4).
 * @param {string} stored
 * @returns {string} a valid descriptor code, or "" if none matches
 */
function normalizeCode(stored) {
  if (!stored) return "";
  if (entry(stored)) return stored;
  const byPack = registry?.languages.find((l) => l.i18n === stored);
  return byPack ? byPack.code : "";
}

/** L1 entries for the onboarding picker (Step 1). @returns {LanguageEntry[]} */
export function motherTongues() {
  return languages().languages.filter((l) => l.availableAsMotherTongue);
}

/**
 * Target *course* options for onboarding Step 2 (Brief 004b). Every language is
 * listed; the language the learner already speaks (`excludeCode`, defaulting to
 * the chosen L1) is greyed "you already speak this", and any language not yet
 * available as a target is greyed "coming soon" (Unity's greyed-button pattern).
 * Availability is data-driven — no hardcoded language codes here (Gotcha 3).
 * @param {string} [excludeCode] the L1 to exclude (defaults to the active L1)
 * @returns {{ code: string, available: boolean, reason: "speaksAlready" | "comingSoon" | null }[]}
 */
export function targetCourses(excludeCode = motherTongue) {
  return languages().languages.map((l) => {
    if (!l.availableAsTarget) return { code: l.code, available: false, reason: "comingSoon" };
    if (l.code === excludeCode) return { code: l.code, available: false, reason: "speaksAlready" };
    return { code: l.code, available: true, reason: null };
  });
}

/** Current axes. @returns {{ motherTongue: string, targetLanguage: string }} */
export function current() {
  return { motherTongue, targetLanguage };
}

/** Both axes chosen + stored? Drives the onboarding skip (Success criterion 4). */
export function hasStoredChoices() {
  try {
    return !!localStorage.getItem(LS_MOTHER) && !!localStorage.getItem(LS_TARGET);
  } catch { return false; }
}

/**
 * Load the registry, hydrate both axes from localStorage (falling back to the
 * registry defaults), and apply the L1's i18n pack. Idempotent boot call; runs
 * before kfe:ready so screens never flash raw keys.
 * @returns {Promise<void>}
 */
export async function initLanguages() {
  const reg = await loadRegistry();
  let storedMother = "", storedTarget = "";
  try {
    storedMother = localStorage.getItem(LS_MOTHER) ?? "";
    storedTarget = localStorage.getItem(LS_TARGET) ?? "";
  } catch { /* localStorage disabled (private mode) — use defaults */ }

  const normMother = normalizeCode(storedMother);
  const normTarget = normalizeCode(storedTarget);
  const hasStoredMother = reg.languages.some((l) => l.code === normMother && l.availableAsMotherTongue);
  const hasStoredTarget = reg.languages.some((l) => l.code === normTarget && l.availableAsTarget);

  motherTongue = hasStoredMother ? normMother : defaultMotherTongueCode();
  targetLanguage = hasStoredTarget ? normTarget : defaultTargetCode();

  // First run (no stored L1): render the opening dialogue in the neutral pack so
  // a fresh visitor isn't greeted in the default L1 (Brief 004a SC#1). Returning
  // users get their chosen L1's pack immediately. The onboarding screen applies
  // the real L1 the moment Step 1 is answered.
  const openingI18n = hasStoredMother ? motherTongueEntry().i18n : NEUTRAL_OPENING_I18N;
  await setLanguage(openingI18n);
}

/**
 * Set the mother tongue (L1): persist, load its i18n pack, fire OnLanguageChanged.
 * @param {string} code
 * @returns {Promise<void>}
 */
export async function setMotherTongue(code) {
  const reg = languages();
  if (!reg.languages.some((l) => l.code === code && l.availableAsMotherTongue)) return;
  motherTongue = code;
  try { localStorage.setItem(LS_MOTHER, code); } catch { /* ignore */ }
  await setLanguage(motherTongueEntry().i18n);
  OnLanguageChanged.dispatchEvent(new Event("change"));
}

/**
 * Set the target language (L2): accepts any `availableAsTarget` language
 * (Brief 004b — no longer English-only). Persist + fire OnLanguageChanged.
 * @param {string} code
 */
export function setTarget(code) {
  const reg = languages();
  if (!reg.languages.some((l) => l.code === code && l.availableAsTarget)) return;
  targetLanguage = code;
  try { localStorage.setItem(LS_TARGET, code); } catch { /* ignore */ }
  OnLanguageChanged.dispatchEvent(new Event("change"));
}

/**
 * Subscribe to language changes; returns an unsubscribe for addCleanup()
 * (Gotcha 4 — never stack handlers).
 * @param {() => void} fn
 * @returns {() => void}
 */
export function onLanguageChanged(fn) {
  const h = () => fn();
  OnLanguageChanged.addEventListener("change", h);
  return () => OnLanguageChanged.removeEventListener("change", h);
}

/**
 * Strip spaces + trailing dots, casing preserved — matches Unity PhraseNoSpaces.
 * e.g. "Stand up." → "Standup".
 * @param {string} phrase
 * @returns {string}
 */
export function PhraseNoSpaces(phrase) {
  return String(phrase).replace(/ /g, "").replace(/\.+$/, "");
}

/**
 * Resolve a language's audio URL for an MMU. English keys off the explicit
 * `audioStem` verbatim (`audio/StandUp.ogg`); Mandarin keys off the English
 * phrase run through PhraseNoSpaces with a `zh_` prefix (`audio/zh_Standup.ogg`)
 * — each encoded in the descriptor so the two are never crossed (Gotcha 2). The
 * Mandarin set doubles as both the L1 scaffold (zh speaker) and the target audio
 * (en speaker learning Mandarin) — same files, both directions.
 * The extension is the browser's best-supported audio format (`audioExt()` —
 * `.m4a` with `.ogg` fallback; Brief 006a), since WebKit can't decode Ogg.
 * @param {LanguageEntry} d
 * @param {Record<string, any>} mmu
 * @returns {string}
 */
function audioUrlFor(d, mmu) {
  const raw = mmu[d.audioKey] ?? "";
  const base = d.audioFromPhrase ? PhraseNoSpaces(raw) : raw;
  return `${mediaBase}audio/${d.audioPrefix}${base}.${audioExt()}`;
}

/**
 * Target (L2) audio URL for an MMU, resolved via the SELECTED target's
 * descriptor (Brief 004b — not hardcoded to English's audioStem).
 * @param {Record<string, any>} mmu
 * @returns {string}
 */
export function targetAudioUrl(mmu) {
  return audioUrlFor(targetEntry(), mmu);
}

/**
 * L1 scaffolding-audio URL, resolved via the active L1's descriptor. Must match
 * the existing R2 files exactly or it 404s (Gotcha 2).
 * @param {Record<string, any>} mmu
 * @returns {string}
 */
export function l1AudioUrl(mmu) {
  return audioUrlFor(motherTongueEntry(), mmu);
}

/**
 * The word shown in the exercise — the SELECTED target's rendering of the phrase
 * (en: `phrase`, zh: `phraseZh`). Empty if the target has no text for this MMU.
 * @param {Record<string, any>} mmu
 * @returns {string}
 */
export function targetText(mmu) {
  const d = targetEntry();
  return (d.textField && mmu[d.textField]) || "";
}

/**
 * The translation scaffold — the L1's rendering of the phrase. Returns "" when
 * L1 === target (the learner already reads the target, so there is nothing to
 * scaffold — Brief 004a #5 / Gotcha 4: suppresses the affordance and never
 * fetches a bare-prefix audio file).
 * @param {Record<string, any>} mmu
 * @returns {string}
 */
export function scaffoldText(mmu) {
  if (motherTongue === targetLanguage) return "";
  const d = motherTongueEntry();
  return (d.textField && mmu[d.textField]) || "";
}
